import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Clock, AlertCircle, CheckCircle, Loader2, X } from '../../icons';
import { listMyTasks, TASK_STATUSES, type MyTaskDto, type MyTasksDueFilter, type TaskStatus } from '../../api/tasks';
import { getProjectHomeSummary, type ProjectHomeSummaryDto } from '../../api/project-home';
import { TaskStatusIcon, Skeleton } from '../../components/shared';
import { useCountUp } from '../../hooks/useCountUp';
import { DetailPageHeader } from './DetailPageHeader';
import { dueDayDiff, dueState } from './my-task-due';
import { tProject, getProjectLang, subscribeProjectLang, type ProjectI18nKey } from '../../lib/project-i18n';
import './ProjectPage.css';

const PAGE_SIZE = 20;
type Tab = 'ALL' | TaskStatus;

/*
 * ★ `?filter=` 거르기 (2026-10-12 · 정본 `MyTasksDetailPage.tsx:24-32` 이관).
 *
 * 정본 머리말 그대로 ⇒ 「`/project/my-tasks?filter=dueSoon|overdue|risk` 로 들어오면
 *   그 조건으로 걸러진 목록을 연다. 종전에는 홈에서 "지연 7" 을 보고도 그 7건으로 갈 방법이
 *   없었다. 필터링은 서버(`/tasks/me?due=`)가 하므로 페이징·총건수가 그대로 맞는다.」
 *
 * ⭐ 쟀다 — 서버·API 는 «이미» 받는다(이 판은 프론트만):
 *   서버 `tasks.routes.ts:1635` ⇒ due: { type: 'string', enum: ['soon','overdue','risk'] }
 *        `:1704` ⇒ buildMyTasksDueWhere({ due: req.query.due, … })
 *   프론트 `api/tasks.ts:430` ⇒ due?: MyTasksDueFilter · `:437` ⇒ 보낸다
 *   ⇒ ⚠ `api/tasks.ts:571` 머리말은 「칸이 아직 없다」로 «낡았다» — 4-5 가 서버 뒤에 넣었다.
 *
 * ⚠ 왜 지금 들이나 — 운영이 그리는 것은 `MyTasksBoard` 이고 그것은 due 를 읽는다(4곳).
 *   ⇒ 그래서 «지금은» 안 깨진다. 그런데 깃발(PROJECT_HOME_AW001)을 끄는 «롤백 경로»에서는
 *     이 화면이 그려지고, 홈의 통계 칸이 보낸 `?filter=` 가 «버려진다» ⇒ 롤백 품질이 깎인다.
 *   ⇒ ⇒ 즉 이 판이 막는 것은 «지금의 결함»이 아니라 «되돌릴 때의 결함»이다.
 */
const FILTER_PARAM: Record<string, MyTasksDueFilter> = {
  dueSoon: 'soon',
  overdue: 'overdue',
  risk: 'risk',
};
const FILTER_LABEL: Record<MyTasksDueFilter, ProjectI18nKey> = {
  soon: 'project.mytasks.filter_due_soon',
  overdue: 'project.mytasks.filter_overdue',
  risk: 'project.mytasks.filter_risk',
};

const STATUS_CLASS: Record<string, string> = {
  TODO: 'pj-badge--todo',
  IN_PROGRESS: 'pj-badge--progress',
  REVIEW: 'pj-badge--review',
  DONE: 'pj-badge--done',
};
const PRIORITY_CLASS: Record<string, string> = {
  HIGH: 'pj-priority--high',
  MEDIUM: 'pj-priority--medium',
  LOW: 'pj-priority--low',
};

// v2.10.1 — 행 좌측 상태 컬러 스트립 (지연 시 red 로 덮어씀)
const ROW_ACCENT: Record<string, string> = {
  TODO: 'var(--text-muted)',
  IN_PROGRESS: 'var(--accent-blue)',
  REVIEW: 'var(--accent-orange)',
  DONE: 'var(--accent-green)',
};

/*
  ⛔ 종전에는 `new Date().toISOString().slice(0,10)` 의 **UTC 날짜**와 문자열을 비교했다.
    KST 00:00~09:00 에는 UTC 날짜가 «아직 전날»이라 「어제 마감」이 지연으로 안 세졌다.
    ⇒ 그 9시간 동안 이 화면의 수가 홈 히어로(서버 집계)·다른 화면과 «어긋났다».
  ⭐ 판정은 `my-task-due` «한 곳»에서 온다 — 로컬 자정 기준이라 그 창이 없다.
    (2026-10-01 · StatsDetailPage 에서 같은 고침을 한 뒤 레포 전체를 세어 찾은 나머지다.)
*/
function isOverdue(dueDate: string | null, status: string): boolean {
  return dueState(dueDate, status) === 'overdue';
}

/** 진행률 % 카운트업 (훅 규칙상 분리 컴포넌트). */
function HeroPct({ pct }: { pct: number }) {
  const shown = useCountUp(pct, 900);
  return <span className="pj-mytasks-hero-pct">{shown}<small>%</small></span>;
}

/**
 * MyTasksDetailPage (project-menu v2 — /project/my-tasks) — 내 할일 카드 드릴다운 상세.
 *
 * 설계 §3.3-C: 상태 필터 탭(전체/4종 — TaskStatusIcon) + /tasks/me 페이징(limit/offset·
 * total, "더 보기" 누적) + 행 클릭 → /task?project= . 마감 지연 강조.
 */
export function MyTasksDetailPage() {
  useSyncExternalStore(subscribeProjectLang, getProjectLang, getProjectLang);
  const navigate = useNavigate();
  /*
   * ⚠ 주소를 «진실의 한 곳»으로 둔다 — 상태로 복사하지 않는다.
   *   복사하면 뒤로가기·공유 링크에서 두 값이 갈린다(정본 :77-78 과 같은 꼴).
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const due = FILTER_PARAM[searchParams.get('filter') ?? ''] ?? null;

  const [tab, setTab] = useState<Tab>('ALL');
  const [items, setItems] = useState<MyTaskDto[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProjectHomeSummaryDto | null>(null);

  // v2.10.1 — 진행 요약 밴드 + 탭별 카운트 (1콜, 실패·미응답 시 조용히 숨김)
  useEffect(() => {
    void (async () => {
      try {
        setSummary((await getProjectHomeSummary()) ?? null);
      } catch {
        /* 밴드·카운트 숨김 (목록 동작과 무관) */
      }
    })();
  }, []);

  /*
   * ⚠ `dueFilter` 를 «인자»로 받는다 — useCallback([]) 안에서 바깥 `due` 를 닫아 쓰면
   *   낡은 값이 박힌다(정본 :99 와 같은 꼴). 그래서 부르는 쪽이 `due` 를 넘긴다.
   */
  const load = useCallback(async (activeTab: Tab, offset: number, append: boolean, dueFilter: MyTasksDueFilter | null) => {
    if (append) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res = await listMyTasks({
        ...(activeTab === 'ALL' ? {} : { status: [activeTab] }),
        limit: PAGE_SIZE,
        offset,
        ...(dueFilter ? { due: dueFilter } : {}),
      });
      setTotal(res.total);
      setItems((prev) => (append ? [...prev, ...res.items] : res.items));
    } catch (err) {
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? (err as Error).message ?? tProject('project.mytasks.err_fetch'));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => { void load(tab, 0, false, due); }, [tab, load, due]);

  const hasMore = items.length < total;

  // v2.10.1 — 진행 요약 (완료/전체 + 임박·지연 칩). summary 미가용 시 밴드 자체를 숨김.
  const myStats = summary?.myTasks;
  const heroPct = myStats && myStats.total > 0
    ? Math.round(((myStats.byStatus.DONE ?? 0) / myStats.total) * 100)
    : 0;
  const tabCount = (t: Tab): number | undefined =>
    t === 'ALL' ? myStats?.total : myStats?.byStatus?.[t];

  return (
    <div className="pj-page">
      <DetailPageHeader title={tProject('project.detail.mytasks.title')} />

      {myStats && (
        <section className="pj-mytasks-hero" aria-label={tProject('project.detail.mytasks.hero_aria')}>
          <HeroPct pct={heroPct} />
          <div className="pj-mytasks-hero-track" aria-hidden="true">
            <span className="pj-mytasks-hero-fill" style={{ width: `${heroPct}%` }} />
          </div>
          <span className="pj-mytasks-hero-label">
            {tProject('project.detail.mytasks.hero_done', { done: myStats.byStatus.DONE ?? 0, total: myStats.total })}
          </span>
          <span className="pj-mytasks-hero-chips">
            {myStats.dueSoon7d > 0 && (
              <span className="pj-dday pj-dday--soon">
                <Clock size={11} aria-hidden="true" /> {tProject('project.detail.stats.due_soon_heading')} {myStats.dueSoon7d}
              </span>
            )}
            {myStats.overdue > 0 && (
              <span className="pj-dday pj-dday--over">
                <AlertCircle size={11} aria-hidden="true" /> {tProject('project.detail.stats.overdue_heading')} {myStats.overdue}
              </span>
            )}
          </span>
        </section>
      )}

      {/* ★ 거른 띠 — 정본 MyTasksDetailPage:161-179 글자 그대로 (2026-10-12 이관) */}
      {due && (
        <div className="pj-mytasks-filterbar" role="status">
          <span className="pj-dday pj-dday--over">
            <AlertCircle size={11} aria-hidden="true" /> {tProject(FILTER_LABEL[due])}
          </span>
          <button
            type="button"
            className="pj-mytasks-filterclear"
            onClick={() => {
              // 파라미터만 지운다 — 탭·스크롤 등 나머지 화면 상태는 그대로 둔다.
              const next = new URLSearchParams(searchParams);
              next.delete('filter');
              setSearchParams(next, { replace: true });
            }}
          >
            <X size={12} aria-hidden="true" /> {tProject('project.mytasks.filter_clear')}
          </button>
        </div>
      )}

      <div className="pj-mytasks-tabs" role="tablist" aria-label={tProject('project.detail.mytasks.tabs_aria')}>
        {(['ALL', ...TASK_STATUSES] as Tab[]).map((t) => {
          const n = tabCount(t);
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={`pj-mytasks-tab${tab === t ? ' pj-mytasks-tab--active' : ''}`}
              onClick={() => setTab(t)}
            >
              {t !== 'ALL' && <TaskStatusIcon status={t} size={12} />}
              {t === 'ALL'
                ? tProject('project.detail.mytasks.tab_all')
                : tProject(`project.status.${t}` as ProjectI18nKey)}
              {n !== undefined && <span className="pj-mytasks-tab-count">{n}</span>}
            </button>
          );
        })}
        <span className="pj-mytasks-total">{tProject('project.detail.mytasks.total', { n: total })}</span>
      </div>

      {error && (
        <div className="pj-error" role="alert">
          <AlertCircle size={14} /> {error}
          <button type="button" className="pj-button" onClick={() => void load(tab, 0, false, due)}>
            {tProject('project.btn.retry')}
          </button>
        </div>
      )}
      {loading && !error && (
        <div className="pj-mytasks-skeleton" role="status">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="pj-mytasks-skeleton-row" style={{ animationDelay: `${i * 80}ms` }}>
              <Skeleton width="40%" height={13} />
              <Skeleton width="20%" height={11} />
            </div>
          ))}
        </div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="pj-empty pj-empty--ok" role="status">
          <CheckCircle size={14} aria-hidden="true" /> {tProject('project.detail.mytasks.empty')}
        </div>
      )}

      {/* key={tab} — 탭 전환 시 리스트 리마운트로 스태거 진입 재생 */}
      <ul key={tab} className="pj-mytask-items pj-mytask-items--detail pj-mytask-items--anim">
        {items.map((t, i) => {
          const overdue = isOverdue(t.dueDate, t.status);
          const statusKey = `project.status.${t.status}` as ProjectI18nKey;
          const priorityKey = `project.priority.${t.priority}` as ProjectI18nKey;
          const days = t.dueDate ? dueDayDiff(t.dueDate) : null;
          return (
            <li key={t.id} style={{ animationDelay: `${Math.min(i % PAGE_SIZE, 10) * 50}ms` }}>
              <button
                type="button"
                className={`pj-mytask-row pj-mytask-row--rich${t.status === 'DONE' ? ' pj-mytask-row--done' : ''}`}
                style={{ '--row-accent': overdue ? 'var(--accent-red)' : ROW_ACCENT[t.status] } as CSSProperties}
                onClick={() => navigate(`/task?project=${t.project.id}`)}
                aria-label={tProject('project.mytasks.open_aria', { title: t.title, project: t.project.name })}
              >
                <span className="pj-mytask-main">
                  <span className="pj-mytask-title">{t.title}</span>
                  <span className="pj-mytask-meta">
                    {t.project.name}
                    {t.project.teamName ? ` · ${t.project.teamName}` : ''}
                  </span>
                </span>
                <span className="pj-mytask-side">
                  <span className={`pj-badge ${STATUS_CLASS[t.status] ?? ''}`}>
                    <TaskStatusIcon status={t.status as TaskStatus} size={11} />
                    {tProject(statusKey)}
                  </span>
                  <span className={`pj-priority ${PRIORITY_CLASS[t.priority] ?? ''}`}>
                    <span className="pj-priority-dot" aria-hidden="true" />
                    {tProject(priorityKey)}
                  </span>
                  {days !== null && t.status !== 'DONE' && (
                    <span className={`pj-dday pj-dday--${days < 0 ? 'over' : days === 0 ? 'today' : days <= 7 ? 'soon' : 'far'}`}>
                      {days < 0
                        ? tProject('project.detail.stats.dday_over', { n: -days })
                        : days === 0
                          ? tProject('project.detail.stats.dday_today')
                          : tProject('project.detail.stats.dday_left', { n: days })}
                    </span>
                  )}
                  {t.dueDate && (
                    <span className={`pj-mytask-due${overdue ? ' pj-mytask-due--overdue' : ''}`}>
                      {overdue ? <AlertCircle size={11} /> : <Clock size={11} />}
                      {tProject('project.mytasks.due', { date: t.dueDate })}
                    </span>
                  )}
                </span>
              </button>
            </li>
          );
        })}
      </ul>

      {hasMore && !loading && (
        <button
          type="button"
          className="pj-button pj-mytasks-more"
          onClick={() => void load(tab, items.length, true, due)}
          disabled={loadingMore}
        >
          {loadingMore ? <Loader2 size={13} className="pj-spin" aria-hidden="true" /> : tProject('project.detail.mytasks.more')}
        </button>
      )}
    </div>
  );
}
